import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SignalScoreConfig {
  id: string;
  numeric_values_weight: number;
  merchant_extracted_weight: number;
  gift_card_keywords_weight: number;
  deal_dates_weight: number;
  recency_bonus_weight: number;
  recency_hours_threshold: number;
  updated_at: string;
}

export function useCommunitySignalScoreConfig() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: config,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["community-signal-score-config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_signal_score_config")
        .select("*")
        .limit(1)
        .single();

      if (error) throw error;
      return data as SignalScoreConfig;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Omit<SignalScoreConfig, 'id' | 'updated_at'>>) => {
      if (!config?.id) throw new Error("No config found");
      
      const { error } = await supabase
        .from("community_signal_score_config")
        .update(updates)
        .eq("id", config.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["community-signal-score-config"] });
      toast({ title: "Score weights updated" });
    },
    onError: (error) => {
      toast({
        title: "Failed to update weights",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  });

  return {
    config,
    isLoading,
    refetch,
    updateConfig: updateMutation.mutate,
    isUpdating: updateMutation.isPending
  };
}
